import React, { useEffect, useState } from 'react';
import {
  MdDirectionsBus,
  MdHotel,
  MdRestaurant,
  MdFlight,
  MdLocalTaxi,
  MdPlace,
  MdShoppingBag,
  MdLocalPlay,
  MdSecurity,
  MdCardMembership,
  MdPhone,
  MdMoreHoriz,
} from 'react-icons/md';

interface Category { value: string; label: string; icon?: string; color?: string }

interface Props {
  categories: Category[];
  selected: string;
  onSelect: (value: string) => void;
}

// react-icons export types are not strictly React.ComponentType in some
// @types/react-icons versions. Use `any` and render via React.createElement
// to avoid JSX typing issues during the web build.
const iconMap: Record<string, any> = {
  bus: MdDirectionsBus,
  hotel: MdHotel,
  restaurant: MdRestaurant,
  flight: MdFlight,
  'local-taxi': MdLocalTaxi,
  place: MdPlace,
  'shopping-bag': MdShoppingBag,
  'local-play': MdLocalPlay,
  security: MdSecurity,
  'card-membership': MdCardMembership,
  phone: MdPhone,
  'more-horiz': MdMoreHoriz,
};

const CategorySelectorWeb: React.FC<Props> = ({ categories, selected, onSelect }) => {
  // For parity with the app layout we force 4 columns on wide screens and
  // ensure the grid is centered. Using fixed column width keeps tiles uniform.
  const gridStyle: React.CSSProperties = {
    ...styles.grid,
    // responsive: let the grid create as many columns as fit, minimum 140px
    gridTemplateColumns: `repeat(auto-fit, minmax(140px, 1fr))`,
    gap: 16,
    width: '100%',
    margin: '0 auto',
    justifyContent: 'center',
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Expense categories</div>
      <div style={styles.cardInner}>
        <div style={gridStyle}>
        {categories.map(cat => {
          const IconComp = cat.icon ? iconMap[cat.icon] : undefined;
          const isSelected = selected === cat.value;
          const bg = isSelected ? (cat.color ? cat.color + '22' : '#eef6ff') : (cat.color ? cat.color + '10' : '#fff');
          const border = isSelected ? '1px solid transparent' : '1px solid #f0f0f0';

          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => onSelect(cat.value)}
              style={{
                ...styles.item,
                border,
                background: isSelected ? (cat.color ? cat.color + '0f' : '#f0f8ff') : '#fff',
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              <div style={{ ...styles.icon, background: bg }}>
                {IconComp ? React.createElement(IconComp, { size: 22, color: cat.color || '#333' }) : '★'}
              </div>
              <div style={styles.label}>{cat.label}</div>
            </button>
          );
        })}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: { background: '#fff', paddingTop: 20, paddingBottom: 20, paddingLeft: 20, paddingRight: 20, boxSizing: 'border-box', borderRadius: 12, marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 12 },
  cardInner: { background: '#fff', padding: '8px 18px', borderRadius: 8, maxWidth: 740, margin: '0 auto' },
  grid: { display: 'grid', gap: 16, justifyContent: 'center' },
  item: {
    width: '100%',
    height: 110,
    padding: '10px 12px',
    borderRadius: 12,
    background: '#fff',
    border: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  itemActive: { borderColor: '#fb6d3a', background: '#fff7f2' },
  icon: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  label: { fontSize: 14, color: '#333', textAlign: 'center', width: '100%', marginTop: 4 },
};

export default CategorySelectorWeb;
